import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { FamilyRelation, TelephonyPhoneNumber } from '@crm/shared/persons/types';
import { Store } from '@shared/core/utils';
import { PersonApiSharedService } from '@crm/shared/persons';
import { PersonFamilyPageSharedService } from '@crm/shared/persons';
import { PersonPageSharedService } from '@crm/shared/persons';
import { EntityId } from '@shared/core/types';

const initialState = {
  phoneNumbers: undefined as TelephonyPhoneNumber[],
  callerUser: undefined as CallerUserInterface,
};

interface CallerUserInterface {
  callerUserId: EntityId;
  taskId: EntityId;
}

class Events {
  loadPhoneNumbers: void;
}

@Injectable({
  providedIn: 'root',
})
export class TelephonyPageSharedService {
  private store = new Store<typeof initialState, Events>(initialState);
  private store$ = this.store.asReadonly();

  constructor(
    private sharedPersonPage: PersonPageSharedService,
    private familyPage: PersonFamilyPageSharedService,
    private personApi: PersonApiSharedService,
  ) {
    this.initPhoneNumbers();
  }

  unload(): void {
    this.store.update(state => ({
      ...state,
      phoneNumbers: initialState.phoneNumbers,
    }));
  }

  loadPhoneNumbers(): void {
    this.store.fire('loadPhoneNumbers');
  }

  setCallState(callerUser: CallerUserInterface): void {
    this.store.setField('callerUser', callerUser);
  }

  getCallState(): Observable<CallerUserInterface> {
    return this.store$.select('callerUser');
  }

  getPhoneNumbers(): Observable<TelephonyPhoneNumber[]> {
    return this.store$.select('phoneNumbers');
  }

  private initPhoneNumbers(): void {
    this.store$
      .on('loadPhoneNumbers')
      .pipe(
        switchMap(() => this.familyPage.getPersonRelation()),
        switchMap(personRelation => {
          if (personRelation === FamilyRelation.parent) {
            return this.sharedPersonPage.getPerson().pipe(map(person => [person as TelephonyPhoneNumber]));
          }

          return this.familyPage.getFamilyMembers().pipe(
            map(members => members.filter(member => member.relation === FamilyRelation.parent)),
            switchMap(parents =>
              forkJoin([
                this.sharedPersonPage.getPerson().pipe(take(1)),
                ...parents.map(parent => this.personApi.getPerson(parent.general.id, 'telephony-page-shared')),
              ]),
            ),
          );
        }),
        map(persons => persons.filter(person => Boolean(person.phone))),
        tap(phoneNumbers => {
          this.store.update(state => ({
            ...state,
            phoneNumbers,
          }));
        }),
      )
      .subscribe();
  }
}
